import { useEffect, useState } from "react";
import { Search, Tag as TagIcon, Ban, ShieldCheck, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [card, setCard] = useState(null);
  const [note, setNote] = useState("");
  const [tags, setTags] = useState([]);

  const loadClients = (query) =>
    api
      .get("/owner/clients", { params: query ? { q: query } : {} })
      .then(({ data }) => setClients(data))
      .catch(() => setClients([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    loadClients();
    api.get("/owner/client-tags").then(({ data }) => setTags(data)).catch(() => setTags([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => loadClients(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const openClient = (client) => {
    setActiveId(client.id);
    api.get(`/owner/clients/${client.id}`).then(({ data }) => setCard(data)).catch(() => setCard(null));
  };

  const refreshCard = () =>
    activeId && api.get(`/owner/clients/${activeId}`).then(({ data }) => setCard(data)).catch(() => {});

  const addNote = async () => {
    if (!note.trim() || !activeId) return;
    await api.post(`/owner/clients/${activeId}/notes`, { body: note });
    setNote("");
    refreshCard();
  };

  const deleteNote = async (noteId) => {
    await api.delete(`/owner/clients/notes/${noteId}`);
    refreshCard();
  };

  const toggleBlock = async () => {
    if (card.is_blocked) await api.delete(`/owner/clients/${activeId}/block`);
    else await api.post(`/owner/clients/${activeId}/block`);
    refreshCard();
    loadClients(q);
  };

  const toggleTrust = async () => {
    if (card.is_trusted) await api.delete(`/owner/clients/${activeId}/trust`);
    else await api.post(`/owner/clients/${activeId}/trust`);
    refreshCard();
  };

  const toggleTag = async (tag) => {
    const has = card.tags.some((t) => t.id === tag.id);
    if (has) await api.delete(`/owner/clients/${activeId}/tags/${tag.id}`);
    else await api.post(`/owner/clients/${activeId}/tags/${tag.id}`);
    refreshCard();
    loadClients(q);
  };

  return (
    <div>
      <h1 className="mb-4 font-montserrat text-2xl font-bold text-slate-900">Clients</h1>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[320px_1fr]">
        <Card className="p-3">
          <div className="relative mb-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or phone…" className="pl-9" />
          </div>
          <div className="max-h-[560px] divide-y divide-border overflow-y-auto">
            {loading ? (
              <p className="p-3 text-sm text-muted-foreground">Loading…</p>
            ) : clients.length === 0 ? (
              <p className="p-3 text-sm text-muted-foreground">No clients yet.</p>
            ) : (
              clients.map((c) => (
                <button
                  key={c.id}
                  onClick={() => openClient(c)}
                  className={`block w-full p-3 text-left hover:bg-muted ${activeId === c.id ? "bg-muted" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-900">
                      {c.full_name}
                      {c.is_blocked && <Ban className="ml-1 inline h-3 w-3 text-red-500" />}
                    </p>
                    <span className="text-xs text-muted-foreground">{c.visit_count} visits</span>
                  </div>
                  <p className="truncate text-xs text-muted-foreground">{c.phone}</p>
                  {c.client_tags?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {c.client_tags.map((t) => (
                        <Badge key={t.id} style={{ backgroundColor: `${t.color}20`, color: t.color }}>
                          {t.name}
                        </Badge>
                      ))}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </Card>

        <Card className="p-5">
          {!card ? (
            <p className="text-sm text-muted-foreground">Select a client to view their card.</p>
          ) : (
            <div>
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-montserrat text-lg font-bold text-slate-900">{card.client.full_name}</h2>
                  <p className="text-sm text-muted-foreground">{card.client.phone} · {card.client.email}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant={card.is_trusted ? "secondary" : "outline"} onClick={toggleTrust}>
                    <ShieldCheck className="h-4 w-4" /> {card.is_trusted ? "Trusted" : "Trust client"}
                  </Button>
                  <Button size="sm" variant={card.is_blocked ? "danger" : "outline"} onClick={toggleBlock}>
                    <Ban className="h-4 w-4" /> {card.is_blocked ? "Unblock" : "Block"}
                  </Button>
                </div>
              </div>

              <div className="mb-4 grid grid-cols-3 gap-2 text-center sm:gap-3">
                <div className="rounded-xl bg-muted p-3">
                  <p className="text-lg font-bold text-slate-900">{card.visit_count}</p>
                  <p className="text-xs text-muted-foreground">Visits</p>
                </div>
                <div className="rounded-xl bg-muted p-3">
                  <p className="text-lg font-bold text-slate-900">{card.total_spent}</p>
                  <p className="text-xs text-muted-foreground">Total spent</p>
                </div>
                <div className="rounded-xl bg-muted p-3">
                  <p className="text-lg font-bold text-slate-900">{card.appointments[0]?.appointment_date ?? "—"}</p>
                  <p className="text-xs text-muted-foreground">Last visit</p>
                </div>
              </div>

              <div className="mb-4">
                <p className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <TagIcon className="h-3 w-3" /> Tags
                </p>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => {
                    const active = card.tags.some((t) => t.id === tag.id);
                    return (
                      <button
                        key={tag.id}
                        onClick={() => toggleTag(tag)}
                        className="rounded-full border px-2.5 py-1 text-xs font-medium"
                        style={
                          active
                            ? { backgroundColor: tag.color, color: "white", borderColor: tag.color }
                            : { borderColor: tag.color, color: tag.color }
                        }
                      >
                        {tag.name}
                      </button>
                    );
                  })}
                  {tags.length === 0 && <p className="text-xs text-muted-foreground">No tags yet — add some in Settings.</p>}
                </div>
              </div>

              <div className="mb-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</p>
                <div className="mb-2 flex gap-2">
                  <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" />
                  <Button size="sm" onClick={addNote}>Add</Button>
                </div>
                <div className="space-y-2">
                  {card.notes.map((n) => (
                    <div key={n.id} className="flex items-start justify-between rounded-xl bg-muted p-3 text-sm">
                      <div>
                        <p className="text-slate-800">{n.body}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {n.author?.full_name} · {new Date(n.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <button onClick={() => deleteNote(n.id)} className="text-muted-foreground hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {card.notes.length === 0 && <p className="text-xs text-muted-foreground">No notes yet.</p>}
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Appointment history</p>
                <div className="space-y-2">
                  {card.appointments.map((a) => (
                    <div key={a.id} className="flex items-center justify-between rounded-xl border border-border p-3 text-sm">
                      <div>
                        <p className="font-medium text-slate-900">
                          {a.services?.map((s) => s.name_snapshot).join(", ") || "Appointment"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {a.appointment_date} · {a.staff?.name ?? "Any staff"}
                        </p>
                      </div>
                      <Badge variant={a.status === "completed" ? "primary" : "default"}>{a.status}</Badge>
                    </div>
                  ))}
                  {card.appointments.length === 0 && <p className="text-xs text-muted-foreground">No appointments yet.</p>}
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
